import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PackageCheck, Clock, Truck, Store, DollarSign, Sun } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate } from "@/lib/utils";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const KPI = ({ icon: Icon, label, value, sub }) => (
  <div className="px-5 py-4 border-l border-slate-100 first:border-l-0">
    <div className="flex items-center gap-2 text-slate-500 text-[13px] font-semibold"><Icon size={16} className="text-sky-600" /> {label}</div>
    <div className="text-[30px] font-extrabold tracking-tight mt-2">{value}</div>
    <div className="text-[12px] mt-0.5 text-slate-400">{sub}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/vendor/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading dashboard…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="vendor-dashboard">
      <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5">
        <Sun size={22} className="text-sky-500" /> Good morning
      </h1>
      <p className="text-slate-500 text-sm mt-1">Here's what's coming in from your contractor customers.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 mt-6 border-t border-b border-slate-100">
        <KPI icon={PackageCheck} label="New Orders" value={data.kpis.new_orders} sub="Received today" />
        <KPI icon={Clock} label="In Fulfillment" value={data.kpis.in_fulfillment} sub="Active" />
        <KPI icon={Store} label="Catalog Products" value={data.kpis.catalog_products} sub="Active listings" />
        <KPI icon={DollarSign} label="Revenue (MTD)" value={fmt(data.kpis.revenue_mtd)} sub="Delivered orders" />
      </div>

      <div className="grid md:grid-cols-2 gap-9 mt-8">
        <div>
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-bold">Incoming Purchase Orders</div>
            <Link to="/vendor/orders" className="text-[12.5px] font-semibold text-sky-600">View All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold"><th className="text-left pb-2">PO #</th><th className="text-left pb-2">Job</th><th className="text-right pb-2">Amount</th><th className="text-left pb-2 pl-3">Status</th></tr></thead>
              <tbody>
                {data.incoming_orders.map((po) => (
                  <tr key={po.id} onClick={() => navigate(`/vendor/orders/${po.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`incoming-po-row-${po.po_number}`}>
                    <td className="py-2.5 border-t border-slate-100 mono text-sky-600 font-medium">{po.po_number}</td>
                    <td className="py-2.5 border-t border-slate-100">{po.job_name || po.requester_name || "—"}</td>
                    <td className="py-2.5 border-t border-slate-100 text-right mono font-medium">{fmt(po.amount)}</td>
                    <td className="py-2.5 border-t border-slate-100 pl-3"><StatusBadge status={po.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="md:pl-9 md:border-l border-slate-100">
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-bold">Open RFQ Requests</div>
            <Link to="/vendor/rfqs" className="text-[12.5px] font-semibold text-sky-600">View All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full mt-3 text-[12.5px]">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold"><th className="text-left pb-2">RFQ #</th><th className="text-left pb-2">Title</th><th className="text-left pb-2 pl-3">Due</th></tr></thead>
              <tbody>
                {data.open_rfqs.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`/vendor/rfqs/${r.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`open-rfq-row-${r.rfq_number}`}>
                    <td className="py-2.5 border-t border-slate-100 mono text-sky-600 font-medium">{r.rfq_number}</td>
                    <td className="py-2.5 border-t border-slate-100">{r.title}</td>
                    <td className="py-2.5 border-t border-slate-100 pl-3 text-slate-500">{r.due_date ? fmtDate(r.due_date) : "—"}</td>
                  </tr>
                ))}
                {data.open_rfqs.length === 0 && <tr><td colSpan={3} className="py-4 text-center text-slate-400">No open RFQs.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-8 pt-7 border-t border-slate-100">
        <div className="text-[15px] font-bold">Recent Activity</div>
        <div className="mt-3 space-y-2.5">
          {data.activity.map((a) => (
            <div key={a.id} className="text-[12.5px] border-t border-slate-100 pt-2.5">{a.action}</div>
          ))}
          {data.activity.length === 0 && <div className="text-slate-400 text-sm">No recent activity.</div>}
        </div>
      </div>
    </div>
  );
}
